import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { v4 as generateUuid } from 'uuid'
import { toast } from 'react-toastify'

import * as attachmentActions from 'agency/redux/modules/attachment'
import * as campaignProposalActions from 'agency/redux/modules/campaignProposal'
import * as dataStoreItemActions from 'agency/redux/modules/dataStoreItem'
import * as digitalPageActions from 'agency/redux/modules/digitalPage'
import * as documentGroupActions from 'agency/redux/modules/documentGroup'

import filteredObjectArraySelector from 'functions/filteredObjectArraySelector'
import isPrintMode from 'agency/digital_renderer/utils/isPrintMode'
import setupDataStore from 'agency/digital_renderer/utils/setupDataStore'
import useSetState from 'hooks/useSetState'
import useUniqueId from 'hooks/useUniqueId'
import { initializeRealtimeSubscription } from 'functions/initializeRealtimeSubscription'

import relatedEntitySelector from 'agency/digital_renderer/utils/relatedEntitySelector'

import AnalyticsTracker from 'components/AnalyticsTracker'
import BuildingAttachmentModal from 'modals/BuildingAttachmentModal'
import BuildingExternalDocumentModal from 'modals/BuildingExternalDocumentModal'
import ImageSelectModal from 'modals/sidebar/ImageSelectModal'

import RealhubDynamic from 'agency/digital_renderer/themes/RealhubDynamic'

// These are legacy templates
// All digital pages should use the dynamic template moving forward
import RealhubMobile from 'components/proposals/legacyThemes/RealhubMobile'
import RealhubPremium from 'components/proposals/legacyThemes/RealhubPremium'
import RealhubSortable from 'components/proposals/legacyThemes/RealhubSortable'
import TheAgency from 'components/proposals/legacyThemes/TheAgency'

import GlobalContext from 'agency/digital_renderer/utils/context'

import campaignImageSelector from './selectors/campaignImageSelector'
import formStateSelector from './selectors/formStateSelector'

const fetchDataStoreItems = (props, options) => {
  const {
    accessToken,
    campaign,
    currentUser,
    loadDataStoreItems,
    loadedDataStoreItems,
    loadingDataStoreItems,
    dataStoreItemErrors,
  } = props

  const { uuid } = options

  if ((currentUser.id || uuid) && !loadedDataStoreItems && !loadingDataStoreItems && !dataStoreItemErrors.length){
    loadDataStoreItems({
      group_key: uuid,
      token: accessToken,
      owner_type: 'Campaign',
      owner_id: campaign.id,
    })
  }
}

const downloadPdf = (props, setState) => {
  const {
    buildCampaignProposalPdf, buildDigitalPagePdf, campaignProposal, dataSourceType, digitalPage,
  } = props

  if (dataSourceType === 'CampaignProposal') buildCampaignProposalPdf(campaignProposal)
  if (dataSourceType === 'DigitalPage') buildDigitalPagePdf(digitalPage)

  setState({
    buildingPdf: true,
    showBuildingAttachmentModal: true,
  })
}

const pdfBuildComplete = (data, props, setState) => {
  const { hydrateAttachment } = props
  const { payload } = data

  hydrateAttachment(payload)

  setState({
    buildingPdf: false,
  })
}

const generateExternalDocument = (props, setState) => {
  const { documentGroup, generateExternalDocument: generateFn } = props

  generateFn(documentGroup, {
    broadcast_channel: 'documentGroups',
    broadcast_completion: true,
    broadcast_event: `documentGroup.${documentGroup.id}.generateExternalDocument.complete`,
  })

  setState({
    buildingExternalDocument: true,
    showBuildingExternalDocumentModal: true,
  })
}

const generateExternalDocumentComplete = (data, props) => {
  const { redirectUrl } = props
  const { payload } = data

  if (!payload.external_document_created){
    toast.warning('Could not generate document.')
    return
  }

  window.location.href = redirectUrl
}

const getProposalTheme = (brand, digitalTemplate) => {
  if (digitalTemplate && digitalTemplate.base_theme){
    return digitalTemplate.base_theme
  }

  if (brand && brand.options && brand.options.proposal_theme){
    return brand.options.proposal_theme
  }

  return 'realhub-mobile'
}

const selectImage = (image, selectedItemId, setState, props) => {
  const {
    updateProposalImage, dataSource, dataSourceType, updateDigitalPageImage,
  } = props

  const options = {
    digital_template_item_id: selectedItemId,
    include_attachment_file: true,
  }

  setState({ loading: true })

  const updateFn = dataSourceType === 'CampaignProposal' ? updateProposalImage : updateDigitalPageImage
  if (updateFn){
    updateFn(dataSource, image, options).then(() => {
      setState({ showImageSelectModal: false, loading: false })
    })
  }
}

const toggleGallery = (key, index, setState) => {
  setState(state => ({
    ...state,
    [key]: !state[key],
    [`${key}SelectedIndex`]: index ? Number(index) : 0,
  }))
}

const regroupFormResponse = (props, options) => {
  const { digitalTemplate, updateItemsForGroupKey } = props

  const { accessToken, uuid, tag } = options

  const params = {
    digital_template_id: digitalTemplate.id,
    group_key: uuid,
    tag,
    token: accessToken,
  }

  const updateParams = {
    group_key: generateUuid(),
  }

  updateItemsForGroupKey(params, updateParams)
}

const submitFormResponse = (props, options) => {
  const {
    dataSource, dataSourceType, digitalTemplate, submitFormResponse: submitFn,
  } = props

  const { accessToken, uuid, tag } = options
  const { form_submission_recipients } = dataSource.options || {}
  const recipients = form_submission_recipients || []

  if (accessToken && uuid){
    submitFn({
      digital_template_id: digitalTemplate.id,
      emails: recipients.map(recipient => recipient.email),
      group_key: uuid,
      tag,
      owner_id: dataSourceType === 'CampaignProposal' ? dataSource.campaign_id : dataSource.subject_id,
      owner_type: dataSourceType === 'CampaignProposal' ? 'Campaign' : dataSource.subject_type,
      token: accessToken,
    })
  }
}

const updateDataStore = (payload, props, options) => {
  const {
    currentUser, dataSource, digitalTemplate, createDataStoreItem,
  } = props

  const { accessToken, uuid } = options

  const {
    behaviour, data, key, label, value,
  } = payload

  const dataStoreItem = {
    data: JSON.stringify(data),
    digital_template_id: digitalTemplate.id,
    editor_id: currentUser.id,
    editor_type: currentUser.id ? 'User' : null,
    group_key: uuid,
    key,
    label,
    owner_id: dataSource.subject_id,
    owner_type: dataSource.subject_type,
    value,
  }

  createDataStoreItem(dataStoreItem, { token: accessToken, behaviour })
}

const registerActiveComponent = (key, value, state, setState) => {
  const { activeComponents } = state

  if (!activeComponents[key]){
    setState({
      activeComponents: {
        ...activeComponents,
        [key]: value,
      },
    })
  }
}

const themes = {
  'realhub-dynamic': RealhubDynamic,
  'realhub-mobile': RealhubMobile,
  'realhub-premium': RealhubPremium,
  'realhub-sortable': RealhubSortable,
  'the-agency': TheAgency,
}

const callbacks = (component, props, state, setState) => {
  const { selectedItemId } = state

  const componentCallbacks = {
    ImageSelectModal: {
      closeModal: () => setState({ showImageSelectModal: false }),
      selectImage: image => selectImage(image, selectedItemId, setState, props),
      setState,
    },
  }

  return componentCallbacks[component] || {}
}

const defaultState = {
  activeComponents: {},
  buildingPdf: false,
  buildingExternalDocument: false,
  imageEntityId: 0,
  imageEntityType: 'Campaign',
  loading: false,
  selectedItemId: null,
  showBuildingAttachmentModal: false,
  showBuildingExternalDocumentModal: false,
  showSelectImageModal: false,
}

const DigitalPage = (props) => {
  const {
    ablyPublicKey,
    acceptCampaignProposal,
    accessToken,
    adCopies,
    agency,
    agents,
    artwork,
    attachments,
    brand,
    campaign,
    campaignDocuments,
    campaignImages,
    campaignInspections,
    campaignProposal,
    campaignSaleMethod,
    clearUniqueIdData,
    contacts,
    creating,
    currentUser,
    dataSource,
    dataSourceType,
    dataStoreItems,
    debugging,
    digitalPage,
    digitalPage: { page_type },
    digitalTemplate,
    digitalTemplatePage,
    documentGroup,
    editing,
    formState,
    images,
    keenProjectId,
    keenWriteKey,
    loadedDataSource,
    mapboxPublicToken,
    mediaLinks,
    pdfAttachment,
    previewing,
    resetCampaignProposalCustomImages,
    resetDigitalPageCustomImages,
    suburb,
    teamMembers,
    teams,
    updateCampaignProposal,
    updateDigitalPage,
    updatingDataSource,
    users,
  } = props

  const [uuid] = useUniqueId({
    persistKey: window.location.pathname,
  })

  useEffect(() => {
    fetchDataStoreItems(props, { uuid })
  }, [currentUser.id, uuid])

  const dataStore = setupDataStore(dataStoreItems)
  const printMode = isPrintMode()

  const [toggleableComponents, setToggleableComponents] = useState({})

  const [state, setState] = useSetState(defaultState)
  const {
    activeComponents,
    buildingExternalDocument,
    buildingPdf,
    imageEntityId,
    imageEntityType,
    showBuildingAttachmentModal,
    showBuildingExternalDocumentModal,
    showImageSelectModal,
  } = state

  const realtimeKey = dataSourceType === 'CampaignProposal' ? 'campaignProposal' : 'digitalPage'
  const collectionKeyTitle = page_type || 'proposal'

  // Real Time Connections
  useEffect(
    () => void initializeRealtimeSubscription(
      ablyPublicKey,
      `${realtimeKey}s`,
      `${realtimeKey}.${dataSource.id}.downloadPreparation.complete`,
      [dataSource.id],
      {
        success: data => pdfBuildComplete(data, props, setState),
      },
    ),
    [dataSource.id],
  )

  useEffect(() => {
    if (page_type === 'agreement'){
      initializeRealtimeSubscription(
        ablyPublicKey,
        'documentGroups',
        `documentGroup.${documentGroup.id}.generateExternalDocument.complete`,
        [dataSource.id],
        {
          success: data => generateExternalDocumentComplete(data, props, setState),
        },
      )
    }
  }, [dataSource.id, documentGroup.id])

  useEffect(() => {
    if (campaign.id){
      setState({
        imageEntityId: campaign.id,
      })
    }
  }, [campaign.id])

  if (!brand.id) return null

  const themeKey = getProposalTheme(brand, digitalTemplate)
  const ProposalTheme = themes[themeKey]

  const themeProps = {
    activeComponents,
    adCopies,
    agency,
    agents,
    apiKeys: {
      mapboxPublicToken,
    },
    artwork,
    attachments,
    brand,
    campaign,
    campaignDocuments,
    campaignImages,
    campaignInspections,
    campaignProposal,
    campaignSaleMethod,
    contacts,
    currentUser,
    customData: dataSource.custom_data || dataSource.data || {},
    dataSource,
    dataSourceType,
    dataStore,
    dataStoreItems,
    digitalPage,
    digitalTemplate,
    digitalTemplatePage,
    documentGroup,
    editing,
    formState,
    globalState: state,
    images,
    isShareLink: !!accessToken,
    loadedDataSource,
    mediaLinks,
    printMode,
    suburbData: suburb.suburb_data || {},
    teamMembers,
    teams,
    toggleableComponents,
    updatingDataSource,
    users,
    uuid,
    callbacks: {
      acceptCampaignProposal,
      clearUniqueIdData: () => clearUniqueIdData(uuid, digitalTemplate.id, accessToken),
      downloadPdf: () => downloadPdf(props, setState),
      generateExternalDocument: () => generateExternalDocument(props, setState),
      registerActiveComponent: (key, value) => registerActiveComponent(key, value, state, setState),
      regroupFormResponse: tag => regroupFormResponse(props, { accessToken, uuid, tag }),
      resetCampaignProposalCustomImages,
      resetDigitalPageCustomImages,
      setGlobalState: setState,
      submitFormResponse: tag => submitFormResponse(props, { accessToken, uuid, tag }),
      toggleGallery: (key, index) => toggleGallery(key, index, setToggleableComponents),
      updateCampaignProposal,
      updateDataStore: payload => updateDataStore(payload, props, { accessToken, uuid }),
      updateDigitalPage,
    },
  }

  // eslint-disable-next-line no-console
  if (debugging) console.log(themeProps)

  const themeComponent = (
    <GlobalContext.Provider value={themeProps}>
      <ProposalTheme {...themeProps} />

      <BuildingAttachmentModal
        attachable={dataSource}
        attachment={pdfAttachment}
        building={buildingPdf}
        closeModalCallback={() => setState({ showBuildingAttachmentModal: false })}
        showModal={showBuildingAttachmentModal}
      />

      <BuildingExternalDocumentModal
        building={buildingExternalDocument}
        callbacks={{ closeModal: () => setState({ showBuildingExternalDocumentModal: false }) }}
        showModal={showBuildingExternalDocumentModal}
      />

      <ImageSelectModal
        callbacks={callbacks('ImageSelectModal', props, state, setState)}
        campaign={campaign}
        creating={creating}
        preselectedEntity={{ id: imageEntityId, type: imageEntityType }}
        showModal={showImageSelectModal}
      />
    </GlobalContext.Provider>
  )

  if (!editing && !previewing && dataSource.id){
    return (
      <AnalyticsTracker
        collectionKey={`${collectionKeyTitle}-engagement`}
        clientConfig={{ projectId: keenProjectId, writeKey: keenWriteKey }}
        trackingMeta={{
          agency: { id: agency.id, title: agency.title },
          agentIds: campaign.agents,
          brand: { id: agency.brand_id },
          campaign: { id: campaign.id },
          digitalPage: { id: digitalPage.id },
          pageType: dataSourceType === 'CampaignProposal' ? 'proposal' : 'digitalPage',
          proposal: { id: campaignProposal.id },
        }}
        trackScrollPosition
        render={() => themeComponent}
      />
    )
  }

  return themeComponent
}

DigitalPage.propTypes = {
  ablyPublicKey: PropTypes.string.isRequired,
  acceptCampaignProposal: PropTypes.func.isRequired,
  adCopies: PropTypes.object.isRequired,
  agency: PropTypes.object.isRequired,
  artwork: PropTypes.object.isRequired,
  attachments: PropTypes.array,
  brand: PropTypes.object.isRequired,
  buildCampaignProposalPdf: PropTypes.func.isRequired,
  buildDigitalPagePdf: PropTypes.func.isRequired,
  campaign: PropTypes.object.isRequired,
  campaignDocuments: PropTypes.array.isRequired,
  campaignImages: PropTypes.array.isRequired,
  campaignInspections: PropTypes.array.isRequired,
  campaignProposal: PropTypes.object.isRequired,
  campaignSaleMethod: PropTypes.object.isRequired,
  clearUniqueIdData: PropTypes.func.isRequired,
  digitalTemplate: PropTypes.object.isRequired,
  digitalTemplatePage: PropTypes.object.isRequired,
  editing: PropTypes.bool,
  keenProjectId: PropTypes.string,
  keenWriteKey: PropTypes.string,
  loadedDataSource: PropTypes.bool.isRequired,
  mapboxPublicToken: PropTypes.string,
  mediaLinks: PropTypes.object.isRequired,
  pdfAttachment: PropTypes.object.isRequired,
  previewing: PropTypes.bool,
  submitFormResponse: PropTypes.func.isRequired,
  updateCampaignProposal: PropTypes.func.isRequired,
  updatingDataSource: PropTypes.bool.isRequired,
}

function mapStateToProps(state){
  const {
    agents, contacts, dataStoreItems, images, mediaLinks, teams, teamMembers, users,
  } = state.entities

  const {
    adCopies,
    agency,
    artwork,
    attachments,
    brand,
    campaign,
    campaignDocuments,
    campaignProposal,
    campaignSaleMethod,
    dataSource,
    dataSourceType,
    digitalPage,
    digitalTemplate,
    digitalTemplatePage,
    documentGroup,
    pdfAttachment,
    suburb,
  } = relatedEntitySelector(state)

  const creating = state.campaignProposals.creating || state.digitalPages.creating
  const campaignImages = campaignImageSelector(state, { campaign })
  const dataStoreItemsArray = Object.values(dataStoreItems)

  return {
    ablyPublicKey: window.dashboardSettings.publicApiKeys.ablyPublicKey,
    accessToken: window.dashboardSettings.token || null,
    adCopies,
    agency,
    agents,
    artwork,
    attachments,
    brand,
    campaign,
    campaignDocuments,
    campaignImages,
    campaignInspections: filteredObjectArraySelector(state, 'campaignInspections', 'campaign_id', campaign.id),
    campaignProposal,
    campaignSaleMethod,
    contacts,
    creating,
    currentUser: state.currentUser.result,
    dataSource,
    dataSourceType,
    dataStoreItemErrors: state.dataStoreItems.errors,
    dataStoreItems: dataStoreItemsArray,
    debugging: window.dashboardSettings.debugging || false,
    digitalPage,
    digitalTemplate,
    digitalTemplatePage,
    documentGroup,
    editing: window.dashboardSettings.editing || false,
    formState: formStateSelector(state, { dataStoreItems: dataStoreItemsArray, digitalTemplate }),
    images,
    keenProjectId: window.dashboardSettings.publicApiKeys.keenProjectId,
    keenWriteKey: window.dashboardSettings.publicApiKeys.keenWriteKey,
    loadedDataSource: state.campaignProposals.loaded || state.digitalPages.loaded,
    loadedDataStoreItems: state.dataStoreItems.loaded,
    loadingDataStoreItems: state.dataStoreItems.loading,
    mapboxPublicToken: window.dashboardSettings.publicApiKeys.mapboxPublicToken,
    mediaLinks,
    pdfAttachment,
    previewing: window.dashboardSettings.previewing || false,
    redirectUrl: window.dashboardSettings.redirect || '',
    suburb,
    teamMembers,
    teams,
    updatingDataSource: state.campaignProposals.updating || state.digitalPages.updating,
    users,
  }
}

const mapDispatchToProps = {
  acceptCampaignProposal: campaignProposalActions.acceptCampaignProposal,
  buildCampaignProposalPdf: campaignProposalActions.buildCampaignProposalPdf,
  buildDigitalPagePdf: digitalPageActions.buildDigitalPagePdf,
  clearUniqueIdData: dataStoreItemActions.destroyItemsForGroupKey,
  createDataStoreItem: dataStoreItemActions.createDataStoreItem,
  generateExternalDocument: documentGroupActions.generateExternalDocument,
  hydrateAttachment: attachmentActions.hydrateAttachment,
  hydrateDocumentGroup: documentGroupActions.hydrateDocumentGroup,
  loadDataStoreItems: dataStoreItemActions.loadDataStoreItems,
  resetCampaignProposalCustomImages: campaignProposalActions.resetCustomImages,
  resetDigitalPageCustomImages: digitalPageActions.resetCustomImages,
  submitFormResponse: dataStoreItemActions.submitResponse,
  updateCampaignProposal: campaignProposalActions.updateCampaignProposal,
  updateDigitalPage: digitalPageActions.updateDigitalPage,
  updateDigitalPageImage: digitalPageActions.createOrUpdateCustomImage,
  updateItemsForGroupKey: dataStoreItemActions.updateItemsForGroupKey,
  updateProposalImage: campaignProposalActions.createOrUpdateCustomImage,
}

export default connect(mapStateToProps, mapDispatchToProps)(DigitalPage)
